import React, { useState } from "react";
import { CHART_1, INK, MUTED, GRID, PAPER, ON_FILL, ramp } from "./palette";
import ModeWord from "./ModeWord";

// ---- coincidence: Krippendorff's coincidence matrix as a shaded grid ----

// Every pair of codes two coders gave the same unit, weighted 1 / (codes on the unit − 1), counted
// both ways (c–k and k–c). Cells are shaded by count on the teal ramp the other tables use; the
// diagonal (matches) has a solid outline and, with ordered values, the one-step near misses a dashed
// one, as in the agreement chart. Each mismatch cell prints the squared distance its pairs cost, and
// a strip under the grid adds the pairs up by how far apart they are. Props come from
// ChartData#coincidence: labels, counts [[weighted pairs]], margins (n_c), distances [[δ², 0–1]]?,
// ordered, alpha?, units?, unitNoun ("clip"), coderNoun ("observer").
// The tokens and the Legend/Tooltip conventions match ConceptChart.jsx.
const W = 640;
const M = { top: 16, right: 16, left: 16 };

// Weighted counts: 43.33, 3, 0.33.
const num = (v) => String(+v.toFixed(2));
// APA style for values that can't exceed 1: ".13", but 1 stays "1".
const fmtR = (x, places = 2) => (x >= 1 ? "1" : x.toFixed(places).replace(/^0\./, "."));
const pct = (v, total) => { const p = (100 * v) / total; return p > 0 && p < 1 ? "<1%" : `${Math.round(p)}%`; };
const range = (xs) => { const lo = Math.min(...xs), hi = Math.max(...xs); return fmtR(lo) === fmtR(hi) ? fmtR(lo) : `${fmtR(lo)}–${fmtR(hi)}`; };
const upperFirst = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const STEPS = ["Same code", "One step apart", "Two steps apart", "Three steps apart"];
const stepName = (d) => STEPS[d] || `${d} steps apart`;

function Legend({ items }) {
	const swatch = (kind, color) => {
		switch (kind) {
			case "line": return <span className="inline-block h-0.5 w-4 rounded" style={{ background: color }} />;
			case "dash": return <span className="inline-block w-4 border-t-2 border-dashed" style={{ borderColor: color }} />;
			default: return <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color }} />;
		}
	};
	return (
		<div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-600">
			{items.map(({ label, color, kind }, i) => (
				<span key={typeof label === "string" ? label : i} className="flex items-center gap-1.5">{swatch(kind, color)}{label}</span>
			))}
		</div>
	);
}

function Tooltip({ x, lines }) {
	const longest = Math.max(...lines.map((l) => String(l).length));
	const width = Math.max(170, Math.round(longest * 6.4) + 16), left = Math.min(Math.max(x + 8, M.left), W - M.right - width);
	return (
		<g pointerEvents="none" transform={`translate(${left},${M.top})`}>
			<rect width={width} height={10 + lines.length * 15} rx="3" fill={PAPER} stroke={GRID} />
			{lines.map((line, i) => (
				<text key={i} x="8" y={17 + i * 15} fontSize="11" fill={i === 0 ? MUTED : INK} fontWeight={i === 0 ? 400 : 600}>{line}</text>
			))}
		</g>
	);
}

export default function CoincidenceChart({ labels = [], counts = [], margins, distances, ordered = true, units, unitNoun = "unit", coderNoun = "coder" }) {
	const [hover, setHover] = useState(null);
	const k = labels.length;
	if (k < 2) return null;
	const n = margins ? margins.reduce((a, b) => a + b, 0) : counts.flat().reduce((a, b) => a + b, 0);
	const most = Math.max(...counts.flat());
	// Grid geometry: value names on the left, a margin column (n_c) after the last value.
	const labelW = Math.max(90, Math.min(150, Math.max(...labels.map((s) => s.length)) * 7 + 16));
	const left = M.left + labelW, top = M.top + 40;
	const colW = Math.min(76, (W - left - M.right - 56) / k), rowH = Math.min(50, colW);
	const gridR = left + colW * k, gridB = top + rowH * k;
	// Pairs, and with distances the disagreement they add, by how many steps apart the two codes are.
	const bands = Array.from({ length: ordered ? k : 2 }, (_, d) => ({ d, pairs: 0, cost: 0, deltas: [] }));
	counts.forEach((row, i) => row.forEach((v, j) => {
		const d = ordered ? Math.abs(i - j) : Math.min(1, Math.abs(i - j));
		bands[d].pairs += v;
		if (distances) { bands[d].cost += v * distances[i][j]; if (i !== j) bands[d].deltas.push(distances[i][j]); }
	}));
	const mismatches = n - bands[0].pairs, disagreement = bands.reduce((s, b) => s + b.cost, 0);
	const strip = { t: gridB + 26, h: distances ? 64 : 50 };
	const height = strip.t + strip.h + 6;
	const stripW = (W - M.left - M.right) / bands.length;
	const outline = (d) => (d === 0 ? { stroke: INK, strokeWidth: 2 } : ordered && d === 1 ? { stroke: INK, strokeWidth: 1.25, strokeDasharray: "4 3" } : { stroke: "none" });
	const bandName = (d) => (ordered ? stepName(d) : d === 0 ? "Same code" : "Different codes");
	const plural = (v, noun) => `${num(v)} ${v === 1 ? noun : `${noun}s`}`;
	const near = bands[1], far = bands.slice(2);
	const farPairs = far.reduce((s, b) => s + b.pairs, 0), farCost = far.reduce((s, b) => s + b.cost, 0);
	const tooltip = hover && (() => {
		const { i, j } = hover, d = Math.abs(i - j), v = counts[i][j];
		return [
			i === j ? `${labels[i]}, from two ${coderNoun}s` : `${labels[i]} with ${labels[j]}`,
			`${num(v)} weighted pairs (${pct(v, n)} of ${num(n)})`,
			i === j ? "A match" : `${bandName(d)}${distances ? `: each pair costs δ² = ${fmtR(distances[i][j], 3)}` : ""}`,
			...(i === j ? [] : [`Its mirror, ${labels[j]} with ${labels[i]}, holds the same pairs`])
		];
	})();
	return (
		<figure>
			<Legend items={[{ label: <span>Pairs of codes on the same {unitNoun}: <ModeWord light="darker" dark="brighter" /> is more</span>, color: CHART_1 },
				{ label: "Same code (a match)", color: INK, kind: "line" },
				...(ordered ? [{ label: "One step apart (a near miss)", color: INK, kind: "dash" }] : [])]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`Coincidence matrix of ${num(n)} pairable codes${units ? ` from ${units} ${unitNoun}s` : ""}. ${bands.map((b) => `${bandName(b.d)}: ${num(b.pairs)} pairs, ${pct(b.pairs, n)}${distances && b.d > 0 ? `, ${pct(b.cost, disagreement)} of the observed disagreement` : ""}`).join("; ")}. Rows: ${labels.map((l, i) => `${l}: ${counts[i].map(num).join(", ")}`).join("; ")}.`}>
				<text x={M.left} y={top - 8} fontSize="11" fill={MUTED}>One code of the pair</text>
				<text x={left} y={M.top + 2} fontSize="11" fill={MUTED}>The other {coderNoun}'s code on the same {unitNoun}</text>
				{labels.map((l, j) => <text key={`c${l}`} x={left + j * colW + colW / 2} y={top - 8} fontSize="11" fill={INK} textAnchor="middle">{l.replace(/^\d+\s+/, "")}</text>)}
				<text x={gridR + 28} y={top - 8} fontSize="11" fill={MUTED} textAnchor="middle">Codes</text>
				{labels.map((l, i) => <text key={`r${l}`} x={left - 8} y={top + i * rowH + rowH / 2} fontSize="12" fill={INK} textAnchor="end" dominantBaseline="middle">{l}</text>)}
				{counts.map((row, i) => row.map((v, j) => {
					const t = most ? v / most : 0, d = Math.abs(i - j);
					const on = hover && ((hover.i === i && hover.j === j) || (hover.i === j && hover.j === i));
					const ink = t > 0.55 ? ON_FILL : INK, cx = left + j * colW + colW / 2, cy = top + i * rowH + rowH / 2;
					const showDelta = distances && d > 0;
					return (
						<g key={`${i}-${j}`} onMouseEnter={() => setHover({ i, j })} onMouseLeave={() => setHover(null)}>
							<rect x={left + j * colW + 2} y={top + i * rowH + 2} width={colW - 4} height={rowH - 4} rx="3" fill={ramp(t)}
								{...(on ? { stroke: INK, strokeWidth: 2.5 } : outline(ordered ? d : Math.min(1, d) && 2))} />
							<text x={cx} y={showDelta ? cy - 6 : cy} fontSize="13" fill={ink} fontWeight={d === 0 ? 600 : 400} textAnchor="middle" dominantBaseline="middle">{num(v)}</text>
							{showDelta && <text x={cx} y={cy + 11} fontSize="10" fill={t > 0.55 ? ON_FILL : MUTED} textAnchor="middle" dominantBaseline="middle">δ² {fmtR(distances[i][j])}</text>}
						</g>
					);
				}))}
				{counts.map((row, i) => (
					<text key={`m${i}`} x={gridR + 28} y={top + i * rowH + rowH / 2} fontSize="12" fill={MUTED} textAnchor="middle" dominantBaseline="middle">{num(margins ? margins[i] : row.reduce((a, b) => a + b, 0))}</text>
				))}
				<line x1={gridR + 10} x2={gridR + 46} y1={gridB + 2} y2={gridB + 2} stroke={GRID} />
				<text x={gridR + 28} y={gridB + 14} fontSize="12" fill={INK} fontWeight="600" textAnchor="middle">{num(n)}</text>
				{/* The pairs added up by distance: matches, near misses, far misses. */}
				<line x1={M.left} x2={W - M.right} y1={strip.t - 8} y2={strip.t - 8} stroke={GRID} />
				{bands.map((b) => {
					const x0 = M.left + b.d * stripW, o = outline(ordered ? b.d : b.d && 2);
					return (
						<g key={`b${b.d}`}>
							<rect x={x0} y={strip.t + 1} width="14" height="10" rx="2" fill={ramp(0.25)} {...o} strokeWidth={o.stroke === "none" ? 0 : 1.25} />
							<text x={x0 + 20} y={strip.t + 10} fontSize="11" fill={MUTED}>{bandName(b.d)}</text>
							<text x={x0} y={strip.t + 28} fontSize="13" fill={INK} fontWeight="600">{num(b.pairs)} <tspan fontWeight="400" fill={MUTED} fontSize="11">pairs ({pct(b.pairs, n)})</tspan></text>
							{distances && (b.d === 0
								? <text x={x0} y={strip.t + 44} fontSize="11" fill={MUTED}>Cost 0</text>
								: <>
									<text x={x0} y={strip.t + 44} fontSize="11" fill={MUTED}>Each costs δ² {range(b.deltas)}</text>
									<text x={x0} y={strip.t + 59} fontSize="11" fill={INK}>{pct(b.cost, disagreement)} of the disagreement</text>
								</>)}
						</g>
					);
				})}
				{tooltip && <Tooltip x={left + hover.j * colW} lines={tooltip} />}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Every pair of codes two {coderNoun}s gave the same {unitNoun} is counted both ways, so each cell's mirror across the diagonal holds the same pairs; a {unitNoun} with m codes weights its pairs 1 ÷ (m − 1), so each code counts once: a row adds up to the pairable codes of that kind, {num(n)} in all.
				{" "}{num(bands[0].pairs)} of the {num(n)} pairs ({pct(bands[0].pairs, n)}) match.
				{ordered && distances && near && mismatches > 0
					? ` Near misses are ${pct(near.pairs, mismatches)} of the mismatches but only ${pct(near.cost, disagreement)} of the observed disagreement, because a one-step miss costs δ² ${range(near.deltas)} on the ordinal scale, against 1 for ${labels[0].replace(/^\d+\s+/, "").toLowerCase()} with ${labels[k - 1].replace(/^\d+\s+/, "").toLowerCase()}. The ${pct(farPairs, mismatches)} of mismatches two or more steps apart carry the other ${pct(farCost, disagreement)}.`
					: ""}
			</figcaption>
		</figure>
	);
}
